import React from 'react';
import { Box } from '@mui/material';
import {
  NavLink as Link,
} from '@diligentcorp/atlas-web-component-global-nav/lib/react.js';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '../../contexts/UserProfileContext';
import DashboardIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Dashboard.js';
import AcademyIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Academy.js';
import CommunityAppIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/CommunityApp.js';
import FrameworksIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Frameworks.js';
import OrganizationIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Organization.js';
import SettingsIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Settings.js';
import AppConfig from '../../AppConfig';
import { ComplianceEducationNavItems } from './ComplianceEducationNavItems';
import {
  contentAuthoringPanelOverrideOnClose,
  isContentAuthoringNavPath,
  isContentAuthoringPanelOpen,
} from './complianceEducationNavPaths';
import '@diligentcorp/atlas-web-component-icons/lib/ai-sparkle-default/index.js';
import '@diligentcorp/atlas-web-component-icons/lib/ai-report-default/index.js';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'hb-global-navigator': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'app-origin'?: string;
        },
        HTMLElement
      >;
      'dil-ai-sparkle-default-icon': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
          variant?: string;
        },
        HTMLElement
      >;
      'dil-ai-report-default-icon': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
          variant?: string;
        },
        HTMLElement
      >;
    }
  }
}

interface GlobalNavigatorProps {
  onLoad?: () => void;
}

export default function GlobalNavigator({ onLoad }: GlobalNavigatorProps): JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navRef = React.useRef<HTMLElement>(null);
  const hasCalledOnLoad = React.useRef(false);
  const {
    canAccessDashboard,
    canAccessCourses,
    canAccessSettings,
    canAccessAccounts,
    canAccessUsers,
    canAccessContentManagement,
    canAccessTemplates,
    canAccessAiRecommendation,
  } = useUserProfile();
  const [authoringPanelOverride, setAuthoringPanelOverride] = React.useState<boolean | null>(
    null,
  );
  const isOnContentAuthoringRoute =
    AppConfig.complianceEducationNavTreeEnabled &&
    canAccessTemplates &&
    isContentAuthoringNavPath(pathname);

  React.useEffect(() => {
    if (!isOnContentAuthoringRoute) {
      setAuthoringPanelOverride(null);
    }
  }, [isOnContentAuthoringRoute]);

  const isInContentAuthoringPanel =
    AppConfig.complianceEducationNavTreeEnabled &&
    canAccessTemplates &&
    isContentAuthoringPanelOpen(pathname, authoringPanelOverride);

  React.useEffect(() => {
    const checkIfReady = async () => {
      try {
        // Wait for the custom element to be defined
        await customElements.whenDefined('hb-global-navigator');
        console.log('[GlobalNavigator] Web component registered');

        const element = navRef.current;
        if (!element) {
          console.log('[GlobalNavigator] Element ref not available, calling onLoad');
          if (onLoad && !hasCalledOnLoad.current) {
            hasCalledOnLoad.current = true;
            onLoad();
          }
          return;
        }

        // Check if shadow DOM already has content
        const checkShadowContent = () => {
          const shadowRoot = element.shadowRoot;
          if (shadowRoot && shadowRoot.childElementCount > 0) {
            console.log('[GlobalNavigator] Shadow DOM has content, fully loaded');
            if (onLoad && !hasCalledOnLoad.current) {
              hasCalledOnLoad.current = true;
              onLoad();
            }
            return true;
          }
          return false;
        };

        // If already loaded, call onLoad immediately
        if (checkShadowContent()) {
          return;
        }

        // Otherwise, use MutationObserver to watch for shadow DOM changes
        console.log('[GlobalNavigator] Setting up MutationObserver for shadow DOM');
        const observer = new MutationObserver(() => {
          if (checkShadowContent()) {
            observer.disconnect();
          }
        });

        // Observe the element for child list changes (when shadow DOM is attached)
        observer.observe(element, {
          childList: true,
          subtree: true,
        });

        // Fallback timeout: if nothing loads after 5 seconds, call onLoad anyway
        const timeoutId = setTimeout(() => {
          console.warn('[GlobalNavigator] Timeout reached, calling onLoad anyway');
          observer.disconnect();
          if (onLoad && !hasCalledOnLoad.current) {
            hasCalledOnLoad.current = true;
            onLoad();
          }
        }, 5000);

        // Cleanup function
        return () => {
          observer.disconnect();
          clearTimeout(timeoutId);
        };
      } catch (error) {
        console.error('[GlobalNavigator] Error waiting for web component:', error);
        if (onLoad && !hasCalledOnLoad.current) {
          hasCalledOnLoad.current = true;
          onLoad();
        }
      }
    };

    const cleanup = checkIfReady();
    return () => {
      if (cleanup instanceof Promise) {
        cleanup.then((fn) => fn && fn());
      }
    };
  }, [onLoad]);

  return (
    <Box className="global-nav-wrapper">
      <hb-global-navigator
        id="global-navigator"
        app-id="compliance-education"
        ref={navRef as any}
        app-origin={window.location.origin}
        display-mode="panel"
        is-public="false"
        enable-app-nav="true"
      >
        {!isInContentAuthoringPanel && canAccessDashboard && (
          <NavLink to="/review-center">
            {({ isActive }) => (
              <Link label="Review Center" url="/review-center" isCurrent={isActive}>
                <DashboardIcon slot="icon" />
              </Link>
            )}
          </NavLink>
        )}
        {!isInContentAuthoringPanel && canAccessCourses && (
          <NavLink to="/courses">
            {({ isActive }) => (
              <Link label="Courses" url="/courses" isCurrent={isActive}>
                <AcademyIcon slot="icon" />
              </Link>
            )}
          </NavLink>
        )}
        {!isInContentAuthoringPanel && canAccessSettings && AppConfig.userSettingsEnabled && (
          <NavLink to="/settings">
            {({ isActive }) => (
              <Link label="Settings" url="/settings" isCurrent={isActive}>
                <SettingsIcon slot="icon" />
              </Link>
            )}
          </NavLink>
        )}
        {!isInContentAuthoringPanel && canAccessAccounts && (
          <NavLink to="/accounts">
            {({ isActive }) => (
              <Link label="Accounts" url="/accounts" isCurrent={isActive}>
                <OrganizationIcon slot="icon" />
              </Link>
            )}
          </NavLink>
        )}
        {!isInContentAuthoringPanel && canAccessUsers && (
          <NavLink to="/operators">
            {({ isActive }) => (
              <Link label="Operators" url="/operators" isCurrent={isActive}>
                <CommunityAppIcon slot="icon" />
              </Link>
            )}
          </NavLink>
        )}
        {AppConfig.complianceEducationNavTreeEnabled ? (
          <ComplianceEducationNavItems
            canAccessContentManagement={canAccessContentManagement}
            canAccessTemplates={canAccessTemplates}
            isInAuthoringPanel={isInContentAuthoringPanel}
            onOpenAuthoringPanel={() => setAuthoringPanelOverride(true)}
            onCloseAuthoringPanel={() =>
              setAuthoringPanelOverride(
                contentAuthoringPanelOverrideOnClose(isOnContentAuthoringRoute),
              )
            }
          />
        ) : (
          <>
            {canAccessTemplates && (
              <NavLink to="/templates">
                {({ isActive }) => (
                  <Link label="Templates" url="/templates" isCurrent={isActive}>
                    <FrameworksIcon slot="icon" />
                  </Link>
                )}
              </NavLink>
            )}
            {canAccessContentManagement && (
              <NavLink to="/manage-content">
                {({ isActive }) => (
                  <Link
                    label={t('nav.compliance_education.customized_content')}
                    url="/manage-content"
                    isCurrent={isActive}
                  >
                    <AcademyIcon slot="icon" />
                  </Link>
                )}
              </NavLink>
            )}
          </>
        )}
        {/* AI recommendation — admins (dev) or presales orgs (e.g. sales-ops) */}
        {!isInContentAuthoringPanel && canAccessAiRecommendation && (
          <NavLink to="/ai-recommendation">
            {({ isActive }) => (
              <Link
                label={t('nav.ai_recommendation')}
                url="/ai-recommendation"
                isCurrent={isActive}
              >
                <dil-ai-sparkle-default-icon
                  slot="icon"
                  size="lg"
                  variant="outlined"
                  aria-hidden="true"
                />
              </Link>
            )}
          </NavLink>
        )}
      </hb-global-navigator>
    </Box>
  );
}
